// frontend/src/components/mcbim/LeftSidebar/BuildingStoryList.tsx
import React from "react";
import {
  Accordion,
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Menu,
  Paper,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { MdSort } from "react-icons/md";
import { FiCheck } from "react-icons/fi";
import { useGetMcbimBuildingStoriesQuery } from "../../../openapi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectBuildingStoriesGroupedByBuilding } from "../designSelector";
import {
  setBuildingStoryListRedux,
  setSelectedBuildingInRedux,
  setFloorAndJobInRedux,
} from "../designSlice";

/* -------------------------------------------------------------------------- */
/*  Small helpers (no custom types)                                            */
/* -------------------------------------------------------------------------- */

const toStringSafe = (v: unknown): string => (v == null ? "" : String(v));

/* -------------------------------------------------------------------------- */
/*  HeaderControls                                                             */
/* -------------------------------------------------------------------------- */

const HeaderControls: React.FC = () => {
  const dispatch = useAppDispatch();
  const buildingStoriesState = useAppSelector((state) => state.design.buildingStoryList);

  const setSearch = (search: string) => {
    dispatch(setBuildingStoryListRedux({ ...buildingStoriesState, search }));
  };

  const setSortMode = (sortMode: "name" | "rooms") => {
    dispatch(setBuildingStoryListRedux({ ...buildingStoriesState, sortMode }));
  };

  return (
    <Group gap="xs" wrap="nowrap" justify="space-between">
      <TextInput
        value={buildingStoriesState.search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        placeholder="Søk på etasjenavn…"
        size="xs"
        style={{ flex: 1 }}
      />
    </Group>
  );
};

/* -------------------------------------------------------------------------- */
/*  FloorCard                                                                  */
/* -------------------------------------------------------------------------- */

const FloorCard: React.FC<{ building: string; story: any }> = ({ building, story }) => {
  const dispatch = useAppDispatch();
  const floorAndJob = useAppSelector((state) => state.design.floorAndJob);

  // Support both old shape (name/globalId/job.jobId/spaceCount) and new generated (buildingStoryName/.../jobId)
  const floorName = toStringSafe(story.name ?? story.buildingStoryName);
  const floorGlobalId = toStringSafe(story.globalId ?? story.buildingStoryGlobalId);
  const jobId = toStringSafe(story.job?.jobId ?? story.jobId);
  const elevation =
    typeof (story.elevation ?? story.buildingStoryElevation) === "number"
      ? (story.elevation ?? story.buildingStoryElevation)
      : undefined;
  const spaceCount = story.spaceCount;

  const isSelected = floorAndJob?.floorName === floorName && floorAndJob?.jobId === jobId;

  const onSelect = () => {
    dispatch(setSelectedBuildingInRedux(building));
    dispatch(
      setFloorAndJobInRedux({
        floorName,
        jobId,
        floorGlobalId,
        elevation,
      })
    );
  };

  return (
    <Card
      withBorder
      radius="sm"
      padding="sm"
      onClick={onSelect}
      style={{ cursor: "pointer" }}
      bg={isSelected ? "blue.0" : undefined}
    >
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="sm" fw={500} lineClamp={1}>
          {floorName}
        </Text>

        {typeof spaceCount === "number" ? (
          <Badge variant="light" size="sm">
            {spaceCount} ROM
          </Badge>
        ) : null}
      </Group>
    </Card>
  );
};

/* -------------------------------------------------------------------------- */
/*  BuildingItem                                                               */
/* -------------------------------------------------------------------------- */

const BuildingItem: React.FC<{ building: string; floors: any[] }> = ({ building, floors }) => {
  const dispatch = useAppDispatch();
  const selectedBuilding = useAppSelector((state) => state.design.selectedBuilding);

  const isActive = selectedBuilding === building;

  return (
    <Accordion.Item value={building}>
      <Accordion.Control onClick={() => dispatch(setSelectedBuildingInRedux(building))}>
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Text fw={700} size="sm" lineClamp={1}>
            {building}
          </Text>

          <Badge variant={isActive ? "filled" : "light"} size="sm">
            {floors.length} ETASJER
          </Badge>
        </Group>
      </Accordion.Control>

      <Accordion.Panel>
        <Stack gap="xs">
          {floors.map((story) => {
            const key = [
              building,
              toStringSafe(story.globalId ?? story.buildingStoryGlobalId),
              toStringSafe(story.job?.jobId ?? story.jobId),
            ].join("_");

            return <FloorCard key={key} building={building} story={story} />;
          })}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

/* -------------------------------------------------------------------------- */
/*  BuildingAccordion                                                          */
/* -------------------------------------------------------------------------- */
const BuildingAccordion: React.FC = () => {
  const dispatch = useAppDispatch();

  const grouped = useAppSelector(selectBuildingStoriesGroupedByBuilding) as any[];
  const selectedBuilding = useAppSelector((state) => state.design.selectedBuilding);

  return (
    <Accordion
      value={selectedBuilding ?? null}
      onChange={(next) => dispatch(setSelectedBuildingInRedux(next))}
      variant="separated"
      radius="md"
    >
      {grouped.map((g) => (
        <BuildingItem key={g.building} building={g.building} floors={g.floors} />
      ))}
    </Accordion>
  );
};

/* -------------------------------------------------------------------------- */
/*  Main                                                                       */
/* -------------------------------------------------------------------------- */

export const BuildingStoryList: React.FC = () => {
  const projectNo = useAppSelector((state) => state.design.projectNo);

  const { isLoading, isError } = useGetMcbimBuildingStoriesQuery(
    { projectNo: projectNo! },
    { skip: !projectNo }
  );

  const grouped = useAppSelector(selectBuildingStoriesGroupedByBuilding) as any[];

  if (!projectNo) {
    return (
      <Text size="xs" c="dimmed">
        Velg et prosjekt for å se etasjer.
      </Text>
    );
  }

  if (isLoading) {
    return (
      <Center h="100%">
        <Loader size="sm" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Text size="xs" c="red">
        Klarte ikke å hente etasjer.
      </Text>
    );
  }

  return (
    <Paper
      withBorder
      radius="md"
      p="sm"
      h="100%"
      style={{ display: "flex", flexDirection: "column" }}
    >
      <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
        <HeaderControls />

        {grouped.length === 0 ? (
          <Text size="xs" c="dimmed">
            Ingen etasjer funnet i dette prosjektet.
          </Text>
        ) : (
          <>
            <Divider />

            {/* Scroll area */}
            <Box style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <BuildingAccordion />
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};
